import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Stack,
  FormHelperText,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { marketingPersonApi, SearchUserListItem, OrganizerAssignment } from '../../../modules/services/marketingPersonService';
import { useAppSelector } from '../../../modules/store/hooks';
import translate from '../../../i18n/translate';
import AlreadyAssignedConfirmDialog from '../AlreadyAssignedConfirmDialog';

const assignmentSchema = Yup.object().shape({
  commissionValue: Yup.number()
    .min(0, 'Commission must be >= 0')
    .required('Commission is required'),
  commissionType: Yup.string()
    .oneOf(['fixed', 'percent'])
    .required('Type is required'),
  referralCommission: Yup.number().min(0, 'Referral commission must be >= 0'),
  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string().required('End date is required'),
});

interface AddAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (assignment: OrganizerAssignment) => void;
  marketingPersonId: number;
}

const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({ open, onClose, onSuccess, marketingPersonId }) => {
  const language = useAppSelector((state) => state.auth.language);
  const lang = language?.split('-')?.[0] || 'en';

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [organizersList, setOrganizersList] = useState<SearchUserListItem[]>([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState<SearchUserListItem | null>(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [alreadyAssignedOpen, setAlreadyAssignedOpen] = useState(false);

  const [searchOrganizers] = marketingPersonApi.useSearchOrganizersListMutation();
  const [addAssignment] = marketingPersonApi.useAddOrganizerAssignmentMutation();

  // Other marketing persons the selected organizer is already bound to.
  const alreadyAssignedTo = (selectedOrganizer?.marketingPersons || []).filter(
    (p) => p.marketingPersonId !== marketingPersonId
  );

  const formik = useFormik({
    initialValues: {
      commissionValue: 0,
      commissionType: 'percent' as 'fixed' | 'percent',
      referralCommission: 0,
      startDate: '',
      endDate: '',
    },
    validationSchema: assignmentSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (!selectedOrganizer) return;
      if (alreadyAssignedTo.length > 0) {
        setAlreadyAssignedOpen(true);
        return;
      }
      await submitAssignment(values);
    },
  });

  const submitAssignment = async (values: typeof formik.values) => {
    if (!selectedOrganizer) return;
    setAlreadyAssignedOpen(false);
    setAdding(true);
    setSubmitError('');
    try {
      const result = await addAssignment({
        marketingPersonId,
        body: {
          organizerId: selectedOrganizer.id,
          commissionValue: values.commissionValue,
          commissionType: values.commissionType,
          referralCommission: values.referralCommission,
          startDate: values.startDate || undefined,
          endDate: values.endDate || undefined,
        },
        language: lang,
      }).unwrap();
      onSuccess(result);
      handleClose();
    } catch (err: any) {
      setSubmitError(err?.data?.message || err?.message || 'Failed to add assignment');
    } finally {
      setAdding(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 1) return;
    setLoading(true);
    setSearched(false);
    setOrganizersList([]);
    setSelectedOrganizer(null);
    try {
      const result = await searchOrganizers({ search: searchQuery }).unwrap();
      setOrganizersList(result);
      setSearched(true);
    } catch (err) {
      console.error('Search failed', err);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrganizer = (org: SearchUserListItem) => {
    setSelectedOrganizer(org);
    setSubmitError('');
  };

  const handleClose = () => {
    setSearchQuery('');
    setOrganizersList([]);
    setSelectedOrganizer(null);
    setSearched(false);
    setSubmitError('');
    setAlreadyAssignedOpen(false);
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle color="warning.main">{translate('marketingPersonsAddAssignment') || 'Add Organizer Assignment'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Typography variant="subtitle2" color="warning.main">
          {translate('marketingPersonsSearchOrganizer') || 'Search Organizer'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            label={translate('marketingPersonsOrganizerSearch')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSearched(false); setSelectedOrganizer(null); }}
            fullWidth
            margin="dense"
            color="warning"
            size="small"
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            disabled={loading || searchQuery.length < 1}
            color="warning"
            sx={{ minWidth: 100, height: 40 }}
          >
            {loading ? <CircularProgress size={20} color="warning" /> : translate('marketingPersonsFind')}
          </Button>
        </Box>

        {searched && organizersList.length > 0 && (
          <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {organizersList.map((org) => (
              <ListItemButton
                key={org.id}
                selected={selectedOrganizer?.id === org.id}
                onClick={() => handleSelectOrganizer(org)}
                sx={{
                  '&.Mui-selected': { backgroundColor: 'rgba(252, 127, 3, 0.1)', borderLeft: '3px solid #fc7f03' },
                  '&.Mui-selected:hover': { backgroundColor: 'rgba(252, 127, 3, 0.15)' },
                }}
              >
                <ListItemIcon>
                  {selectedOrganizer?.id === org.id ? (
                    <CheckCircleIcon color="warning" />
                  ) : (
                    <PersonIcon color="warning" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={org.name || org.phone}
                  secondary={org.name ? org.phone : undefined}
                />
                <Chip label={`ID: ${org.id}`} size="small" variant="outlined" color="warning" />
              </ListItemButton>
            ))}
          </List>
        )}

        {searched && organizersList.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography>{translate('marketingPersonsSearchNoResult')}</Typography>
          </Alert>
        )}

        {selectedOrganizer && (
          <>
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography>
                {translate('marketingPersonsOrganizerSelected') || 'Selected'}: {selectedOrganizer.name || selectedOrganizer.phone} (ID: {selectedOrganizer.id})
              </Typography>
            </Alert>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="warning.main">
              {translate('marketingPersonsAssignmentParams') || 'Assignment Parameters'}
            </Typography>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <TextField
                name="commissionValue"
                label={translate('marketingPersonsCommission') || 'Commission'}
                type="number"
                size="small"
                color="warning"
                fullWidth
                inputProps={{ min: 0 }}
                value={formik.values.commissionValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.commissionValue && Boolean(formik.errors.commissionValue)}
                helperText={formik.touched.commissionValue && formik.errors.commissionValue}
              />
              <FormControl
                size="small"
                color="warning"
                fullWidth
                error={formik.touched.commissionType && Boolean(formik.errors.commissionType)}
              >
                <InputLabel>Type</InputLabel>
                <Select
                  name="commissionType"
                  value={formik.values.commissionType}
                  label="Type"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="percent">Percent (%)</MenuItem>
                </Select>
                {formik.touched.commissionType && formik.errors.commissionType && (
                  <FormHelperText>{formik.errors.commissionType}</FormHelperText>
                )}
              </FormControl>
              <TextField
                name="referralCommission"
                label={translate('marketingPersonsReferralCommission')}
                type="number"
                size="small"
                color="warning"
                fullWidth
                inputProps={{ min: 0 }}
                value={formik.values.referralCommission}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.referralCommission && Boolean(formik.errors.referralCommission)}
                helperText={
                  (formik.touched.referralCommission && formik.errors.referralCommission) ||
                  translate('marketingPersonsReferralCommissionHint')
                }
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={translate('marketingPersonsStartDate')}
                  value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                  onChange={(value: Dayjs | null) => {
                    formik.setFieldValue('startDate', value ? value.format('YYYY-MM-DD') : '');
                    formik.setFieldTouched('startDate', true, false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="startDate"
                      size="small"
                      color="warning"
                      fullWidth
                      error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                      helperText={formik.touched.startDate && formik.errors.startDate}
                    />
                  )}
                />
                <DatePicker
                  label={translate('marketingPersonsEndDate')}
                  minDate={formik.values.startDate ? dayjs(formik.values.startDate) : undefined}
                  value={formik.values.endDate ? dayjs(formik.values.endDate) : null}
                  onChange={(value: Dayjs | null) => {
                    formik.setFieldValue('endDate', value ? value.format('YYYY-MM-DD') : '');
                    formik.setFieldTouched('endDate', true, false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="endDate"
                      size="small"
                      color="warning"
                      fullWidth
                      error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                      helperText={formik.touched.endDate && formik.errors.endDate}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>
          </>
        )}
        {submitError && <Alert severity="error">{submitError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="warning">{translate('marketingPersonsCancel')}</Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          disabled={adding || !selectedOrganizer || !formik.isValid || !formik.dirty}
          color="warning"
        >
          {adding ? translate('marketingPersonsAdding') : translate('marketingPersonsAdd')}
        </Button>
      </DialogActions>

      <AlreadyAssignedConfirmDialog
        open={alreadyAssignedOpen}
        organizerName={selectedOrganizer?.name || selectedOrganizer?.phone}
        assignedTo={alreadyAssignedTo}
        onCancel={() => setAlreadyAssignedOpen(false)}
        onConfirm={() => submitAssignment(formik.values)}
        confirming={adding}
      />
    </Dialog>
  );
};

export default AddAssignmentModal;
